import { Injectable, inject } from "@angular/core";
import { ProductService } from "@domain/product/product.service";
import { FlameDetectorConfiguration } from "@domain/project/configurations/flame-detector-configuration";
import { AttachmentDataService } from "@domain/project/product-data/attachment-data.service";
import { ProductData } from "@domain/project/product-data/product-data";
import { ConfigProductMap } from "@domain/project/product-list/config-product-map";
import { ProductListDiscountSlot } from "@lib/dtos/project/product-list-discount.dto";
import { Observable, catchError, defaultIfEmpty, forkJoin, map, of } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class FlameDetectorDataService {
  private productService = inject(ProductService);
  private attachmentDataService = inject(AttachmentDataService);

  getFlameDetectorData(
    flameDetectors: readonly FlameDetectorConfiguration[],
    incompleteConfigIds: string[],
    localeId: string,
  ): Observable<ProductData[][]> {
    if (flameDetectors.length == 0) {
      return of([]);
    }
    return forkJoin(flameDetectors.map((flameDetector) => this.getProducts(flameDetector, incompleteConfigIds, localeId)));
  }

  assignProductToConfiguration(
    configs: FlameDetectorConfiguration[],
    uniqueConfigs: FlameDetectorConfiguration[],
    incompleteConfigIds: string[],
    localeId: string,
  ): Observable<ConfigProductMap[]> {
    if (uniqueConfigs.length === 0) {
      return of([]);
    }
    uniqueConfigs.sort((a, b) => parseInt(a.positionNumber!) - parseInt(b.positionNumber!));
    return forkJoin(
      uniqueConfigs.map((uniqueConfig) =>
        this.getProducts(uniqueConfig, incompleteConfigIds, localeId).pipe(
          map((productData) => {
            const placedProductsCount = configs.filter((config) => config.id === uniqueConfig.id).length;
            return new ConfigProductMap(uniqueConfig, productData, placedProductsCount);
          }),
        ),
      ),
    );
  }

  private getProducts(
    flameDetectorConfig: FlameDetectorConfiguration,
    incompleteConfigIds: string[],
    localeId: string,
  ): Observable<ProductData[]> {
    const attachmentStartingIndex = 0;
    const flameDetector$ = this.getFlameDetectorProductData(flameDetectorConfig, incompleteConfigIds, localeId);

    const attachments$: Observable<ProductData[]> = this.attachmentDataService.getProductData(
      flameDetectorConfig,
      flameDetectorConfig.attachments,
      localeId,
      attachmentStartingIndex,
    );

    return forkJoin([flameDetector$, attachments$]).pipe(
      map((results: any[]) => {
        const flameDetectorData = results[0];
        const attachmentData = results[1];

        const productData = [flameDetectorData];
        productData.push(...attachmentData);
        return productData.filter((product): product is ProductData => !!product);
      }),
    );
  }

  private getFlameDetectorProductData(
    flameDetectorConfig: FlameDetectorConfiguration,
    incompleteConfigIds: string[],
    localeId: string,
  ) {
    return this.productService.getProductById(flameDetectorConfig.variantId).pipe(
      map((product) => {
        if (!product) {
          return undefined;
        }
        const productData = ProductData.create(
          product,
          localeId,
          this.productService.getFilterCountry()!,
          1,
          ProductListDiscountSlot.MAIN,
        );
        productData.positionId = flameDetectorConfig.positionNumber!;
        productData.isIncompleteConfiguration = incompleteConfigIds.includes(flameDetectorConfig.id);
        return productData;
      }),
      defaultIfEmpty(undefined),
      catchError(() => of(undefined)),
    );
  }
}
